import { defineStore } from "pinia"
import { type ApiResponse } from "~/types/api"
import { type Currency } from "~/models/currency"
import type { Wallet } from "~/models/wallet"

export const useCurrencyStore = defineStore("currency", () => {
	const app = useNuxtApp()

	const state = {
		currencies: ref<Currency[] | null>(null),
		depositPaymentMethods: ref([
			{
				id: "card-to-card",
				title: app.$i18n.t("card_to_card"),
				icon: "majesticons:creditcard"
			},
			{
				id: "bank-gateway",
				title: app.$i18n.t("payment_gateway"),
				icon: "majesticons:creditcard"
			}
		]),
		paymentMethods: ref([
			{
				id: "card-to-card",
				title: app.$i18n.t("card_to_card"),
				icon: "majesticons:creditcard",
				type: ["fiat"]
			},
			{
				id: "bank-gateway",
				title: app.$i18n.t("payment_gateway"),
				icon: "majesticons:creditcard",
				type: ["fiat"]
			},
			{
				id: "internal-wallet",
				title: app.$i18n.t("internal_wallet"),
				icon: "lets-icons:wallet-alt-fill",
				type: ["fiat", "crypto"]
			},
			{
				id: "external-wallet",
				title: app.$i18n.t("external_wallet"),
				icon: "lets-icons:wallet-alt-fill",
				type: ["crypto"]
			}
		]),
		receivingMethods: ref([
			{
				id: "bank",
				title: app.$i18n.t("bank_card"),
				icon: "majesticons:creditcard",
				type: ["fiat"]
			},
			{
				id: "internal-wallet",
				title: app.$i18n.t("internal_wallet"),
				icon: "lets-icons:wallet-alt-fill",
				type: ["fiat", "crypto"]
			},
			{
				id: "external-wallet",
				title: app.$i18n.t("external_wallet"),
				icon: "lets-icons:wallet-alt-fill",
				type: ["crypto"]
			}
		])
	}

	const getSoldCurrency = (currency?: Currency | null) => {
		return currency
	}

	const getBoughtCurrency = (currency?: Currency | null) => {
		return currency
	}

	const createCurrency = async (formData: CreateCurrencyRequest) => {
		const data = await $fetch<CreateCurrencyResponse>(CreateCurrency.url(), {
			...useFetchConfig(),
			method: CreateCurrency.method,
			body: formData
		})
		return data.data
	}

	const editCurrency = async (formData: EditCurrencyRequest) => {
		const data = await $fetch<EditCurrencyResponse>(EditCurrency.url(), {
			...useFetchConfig(),
			method: EditCurrency.method,
			body: formData
		})
		return data.data
	}

	const updateCurrencyPrice = async (formData: UpdateCurrencyPriceRequest) => {
		const data = await $fetch<UpdateCurrencyPriceResponse>(UpdateCurrencyPrice.url(), {
			...useFetchConfig(),
			method: UpdateCurrencyPrice.method,
			body: formData
		})
		return data.data
	}

	const getAllCurrencies = async () => {
		const data = await $fetch<GetAllCurrenciesResponse>(GetAllCurrencies.url(), {
			...useFetchConfig(),
			method: GetAllCurrencies.method
		})
		return data.data
	}

	const getCurrencyPrices = async (params?: GetCurrencyPricesRequest) => {
		const data = await $fetch<GetCurrencyPricesResponse>(GetCurrencyPrices.url(), {
			...useFetchConfig(),
			method: GetCurrencyPrices.method,
			params
		})
		return data.data
	}

	return {
		...state,
		getSoldCurrency,
		getBoughtCurrency,
		createCurrency,
		editCurrency,
		getAllCurrencies,
		getCurrencyPrices,
		updateCurrencyPrice
	}
})

// currency :: update currency price
export interface UpdateCurrencyPriceRequest {
	id: number
	today_price: number
	auto_update_price: boolean
	extra_price: number
}
export interface UpdateCurrencyPriceResponse extends ApiResponse {
	data: Currency
}
const UpdateCurrencyPrice = {
	method: "POST" as const,
	url: () => `currencies/update-price`
}

// currency :: create currency
export interface CreateCurrencyRequest {
	name: string
	slug: string
	price_api_slug?: string
	symbol: string
	blockchain_id: number | null
	image_id?: number | null
	can_sell?: boolean
	can_buy?: boolean
	type: "fiat" | "crypto"
	status: "enable" | "disable"
	receiving_address?: string
}
export interface CreateCurrencyResponse extends ApiResponse {
	data: Currency
}
const CreateCurrency = {
	method: "POST" as const,
	url: () => `currencies/create`
}

// currency :: edit currency
export interface EditCurrencyRequest {
	id: number
	name?: string
	slug?: string
	price_api_slug?: string
	symbol?: string
	blockchain_id?: number | null
	image_id?: number | null
	can_sell?: boolean
	can_buy?: boolean
	type?: "fiat" | "crypto"
	status?: "enable" | "disable"
	receiving_address?: string
}
export interface EditCurrencyResponse extends ApiResponse {
	data: Currency
}
const EditCurrency = {
	method: "POST" as const,
	url: () => `currencies/edit`
}

// currency :: get all currencies
export interface GetAllCurrenciesRequest {}
export interface GetAllCurrenciesResponse extends ApiResponse {
	data: Currency[]
}
const GetAllCurrencies = {
	method: "GET" as const,
	url: () => `currencies/all`
}

// currency :: get currency prices
export interface GetCurrencyPricesRequest {
	prices?: boolean
	balances?: boolean
	wallets?: boolean
}
export interface GetCurrencyPricesResponse extends ApiResponse {
	data: {
		prices: {
			[key: string]: number
		}
		balances: {
			[key: string]: number
		} | null
		wallets: Wallet[] | null
	}
}
const GetCurrencyPrices = {
	method: "GET" as const,
	url: () => `currencies/prices`
}

export const useCurrencyApiUrl = () => ({
	EditCurrency,
	GetAllCurrencies,
	GetCurrencyPrices
})
